/**
 * Scrolling utility functions for search navigation
 * Handles scrolling to sections, table rows, and highlighted marks
 */

import { RefObject, MutableRefObject } from 'react';

// Scroll timing constants
const SECTION_RETRY_DELAY = 100; // ms
const MAX_SECTION_RETRIES = 3;
const MARK_RETRY_BASE_DELAY = 100; // ms, multiplied by retry count
const MAX_MARK_RETRIES = 5;
const TABLE_SCROLL_DELAY_VISIBLE = 100; // ms when table is visible
const TABLE_SCROLL_DELAY_HIDDEN = 400; // ms when table is hidden
const ROW_FIND_RETRY_DELAY = 200; // ms
const MAX_ROW_FIND_RETRIES = 3;
const SCROLL_PADDING = 20; // px padding from container edge
const ESTIMATED_ROW_HEIGHT = 56; // px default row height
const ROW_BATCH_SIZE = 5; // number of rows to scroll when adjusting

/**
 * Check if an element is fully visible within a container
 */
export const isElementVisible = (element: Element, container: Element): boolean => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  return (
    elementRect.top >= containerRect.top &&
    elementRect.bottom <= containerRect.bottom &&
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right
  );
};

/**
 * Scroll to an element if it's not already visible
 */
export const scrollToElementIfNotVisible = (
  element: Element,
  container: Element,
  options: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' },
): void => {
  if (!isElementVisible(element, container)) {
    // Find the actual scrollable element
    // First check if container itself is scrollable, otherwise look for scrollable children
    let scrollableContainer: HTMLElement | null = null;

    // Check for Virtuoso scroller
    const virtuosoScroller = container.querySelector(
      '[data-test-id="virtuoso-scroller"]',
    ) as HTMLElement;
    if (virtuosoScroller) {
      scrollableContainer = virtuosoScroller;
    } else {
      // Find the closest scrollable parent by checking overflow and scrollHeight
      let current = element.parentElement;
      while (current && current !== container.parentElement) {
        const style = window.getComputedStyle(current);
        const hasOverflow =
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll';
        const isScrollable = hasOverflow && current.scrollHeight > current.clientHeight;

        if (isScrollable) {
          scrollableContainer = current;
          break;
        }
        current = current.parentElement;
      }

      // Fallback to container if no scrollable parent found
      if (!scrollableContainer) {
        scrollableContainer = container as HTMLElement;
      }
    }

    // Manually scroll the container instead of using scrollIntoView to prevent ancestor scroll
    const elementRect = element.getBoundingClientRect();
    const containerRect = scrollableContainer.getBoundingClientRect();

    // Calculate target scroll position relative to container's scrollable area
    const elementTop = elementRect.top - containerRect.top + scrollableContainer.scrollTop;
    const containerHeight = containerRect.height;
    const elementHeight = elementRect.height;

    // Center the element in the container
    const targetScroll = elementTop - containerHeight / 2 + elementHeight / 2;

    scrollableContainer.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: options.behavior || 'smooth',
    });
  }
};

/**
 * Scroll to a specific section by data-section attribute
 */
export const scrollToSection = ({
  scrollContainerRef,
  section,
  retryCount = 0,
  instant = false,
}: {
  scrollContainerRef:
    | RefObject<HTMLElement>
    | MutableRefObject<HTMLElement | undefined>
    | undefined;
  section: string;
  retryCount?: number;
  instant?: boolean;
}) => {
  const scrollContainer = scrollContainerRef?.current;
  if (!scrollContainer) {
    return;
  }

  // Find the section element within the scroll container
  const sectionElement = scrollContainer.querySelector(`[data-section="${section}"]`);
  if (!sectionElement) {
    // Retry up to MAX_SECTION_RETRIES times
    if (retryCount < MAX_SECTION_RETRIES) {
      setTimeout(
        () => scrollToSection({ scrollContainerRef, section, retryCount: retryCount + 1, instant }),
        SECTION_RETRY_DELAY,
      );
    }
    return;
  }

  // Calculate the position of the section relative to the scroll container
  const containerRect = scrollContainer.getBoundingClientRect();
  const sectionRect = sectionElement.getBoundingClientRect();
  const currentScrollTop = scrollContainer.scrollTop;

  // Calculate how much we need to scroll with padding
  const targetScrollTop = currentScrollTop + (sectionRect.top - containerRect.top) - SCROLL_PADDING;

  // Scroll the container to the section with some padding
  scrollContainer.scrollTo({
    top: Math.max(0, targetScrollTop), // Ensure non-negative
    behavior: instant ? 'auto' : 'smooth',
  });
};

/**
 * Scroll to a highlighted mark in a specific section
 * Generic function that works for any section with highlighted search results
 */
export const scrollToSectionMark = ({
  scrollContainerRef,
  section,
  occurrenceIndex,
  retryCount = 0,
  instant = false,
}: {
  scrollContainerRef:
    | RefObject<HTMLElement>
    | MutableRefObject<HTMLElement | undefined>
    | undefined;
  section: string;
  occurrenceIndex?: number;
  retryCount?: number;
  instant?: boolean;
}): void => {
  const scrollContainer = scrollContainerRef?.current;
  if (!scrollContainer) return;

  const sectionElement = scrollContainer.querySelector(`[data-section="${section}"]`);
  if (!sectionElement) {
    // Retry up to MAX_MARK_RETRIES times with increasing delays
    if (retryCount < MAX_MARK_RETRIES) {
      setTimeout(
        () =>
          scrollToSectionMark({
            scrollContainerRef,
            section,
            occurrenceIndex,
            retryCount: retryCount + 1,
            instant,
          }),
        MARK_RETRY_BASE_DELAY * (retryCount + 1),
      );
    }
    return;
  }

  // Find the current match (the one with search-highlight-current class)
  let targetMark = sectionElement.querySelector('mark.search-highlight-current') as Element | null;

  // Fallback: use occurrence index if we can't find the current mark
  if (!targetMark && occurrenceIndex !== undefined) {
    const markElements = Array.from(sectionElement.querySelectorAll('mark'));
    targetMark = markElements[occurrenceIndex] || null;
  }

  // If no mark found yet, check if marks exist at all
  if (!targetMark) {
    const anyMarks = sectionElement.querySelectorAll('mark');
    if (anyMarks.length === 0 && retryCount < MAX_MARK_RETRIES) {
      // Marks not rendered yet, retry
      setTimeout(
        () =>
          scrollToSectionMark({
            scrollContainerRef,
            section,
            occurrenceIndex,
            retryCount: retryCount + 1,
            instant,
          }),
        MARK_RETRY_BASE_DELAY * (retryCount + 1),
      );
      return;
    }
  }

  // Scroll to the mark if found
  if (targetMark) {
    scrollToElementIfNotVisible(targetMark, scrollContainer, {
      behavior: instant ? 'auto' : 'smooth',
      block: 'center',
    });
  } else if (retryCount < MAX_MARK_RETRIES) {
    // Target mark not found, retry
    setTimeout(
      () =>
        scrollToSectionMark({
          scrollContainerRef,
          section,
          occurrenceIndex,
          retryCount: retryCount + 1,
          instant,
        }),
      MARK_RETRY_BASE_DELAY * (retryCount + 1),
    );
  }
};
/**
 * Scroll to a specific row in a virtualized table
 */
export const scrollToTableRow = ({
  scrollContainerRef,
  tableType,
  rowIndex,
  retryCount = 0,
}: {
  scrollContainerRef:
    | RefObject<HTMLElement>
    | MutableRefObject<HTMLElement | undefined>
    | undefined;
  tableType: string;
  rowIndex?: number;
  retryCount?: number;
}) => {
  const scrollContainer = scrollContainerRef?.current;
  if (!scrollContainer) {
    return;
  }

  // Find the table container within the scroll container
  const tableElement = scrollContainer.querySelector(`[data-table-type="${tableType}"]`);
  if (!tableElement) {
    // Retry if table not found yet (might still be expanding)
    if (retryCount < MAX_SECTION_RETRIES) {
      setTimeout(
        () => scrollToTableRow({ scrollContainerRef, tableType, rowIndex, retryCount: retryCount + 1 }),
        SECTION_RETRY_DELAY,
      );
    }
    return;
  }

  // If we have a rowIndex, try to scroll to the specific row in Virtuoso
  if (rowIndex !== undefined) {
    // First, ensure the table is visible by scrolling the main container
    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = tableElement.getBoundingClientRect();
    const scrollTop = scrollContainer.scrollTop;

    // Check if table is well-positioned in the viewport (top is visible with some margin)
    const tableTopInView =
      elementRect.top >= containerRect.top &&
      elementRect.top < containerRect.top + containerRect.height * 0.3;

    const needsParentScroll = !tableTopInView;
    const delayBeforeVirtuosoScroll = needsParentScroll
      ? TABLE_SCROLL_DELAY_HIDDEN
      : TABLE_SCROLL_DELAY_VISIBLE;

    if (needsParentScroll) {
      const targetScroll = scrollTop + elementRect.top - containerRect.top - SCROLL_PADDING;
      scrollContainer.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    }

    // Now use Virtuoso's scrollToIndex API to scroll to the specific row
    // Wait longer if parent needed to scroll first
    setTimeout(() => {
      const stackWithRef = tableElement.querySelector('[data-table-ref]');
      const virtuosoRef = stackWithRef ? (stackWithRef as any).__virtuosoRef : null;

      if (virtuosoRef?.scrollToIndex) {
        virtuosoRef.scrollToIndex({
          index: rowIndex,
          align: 'center',
          behavior: 'smooth',
        });

        // After Virtuoso scrolls, ensure the row is visible in the main container
        setTimeout(() => {
          scrollRowIntoMainContainer(scrollContainer, tableElement, rowIndex);
        }, TABLE_SCROLL_DELAY_HIDDEN);
      } else {
        const virtuosoScroller = tableElement.querySelector('[data-virtuoso-scroller="true"]');
        if (virtuosoScroller) {
          fallbackScrollToRow(virtuosoScroller, rowIndex);

          // After fallback scroll, ensure the row is visible in the main container
          setTimeout(() => {
            scrollRowIntoMainContainer(scrollContainer, tableElement, rowIndex);
          }, TABLE_SCROLL_DELAY_HIDDEN);
        }
      }
    }, delayBeforeVirtuosoScroll);
    return;
  }

  // Fallback: scroll to the table itself
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = tableElement.getBoundingClientRect();
  const scrollTop = scrollContainer.scrollTop;

  const targetScroll =
    scrollTop +
    elementRect.top -
    containerRect.top -
    containerRect.height / 2 +
    elementRect.height / 2;

  scrollContainer.scrollTo({
    top: targetScroll,
    behavior: 'smooth',
  });
};

/**
 * After Virtuoso scrolls to a row, ensure that row is visible in the main scroll container
 */
const scrollRowIntoMainContainer = (
  scrollContainer: HTMLElement,
  tableElement: Element,
  rowIndex: number,
): void => {
  // Find the row by data-index attribute
  const row = tableElement.querySelector(`[data-index="${rowIndex}"]`);
  if (!row) {
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const rowRect = row.getBoundingClientRect();

  // Check if row is visible in the main container
  const isRowVisible =
    rowRect.top >= containerRect.top &&
    rowRect.bottom <= containerRect.bottom;

  if (!isRowVisible) {
    // Calculate scroll position to center the row in the main container
    const rowTop = rowRect.top - containerRect.top + scrollContainer.scrollTop;
    const targetScroll = rowTop - containerRect.height / 2 + rowRect.height / 2;

    scrollContainer.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth',
    });
  }
};

/**
 * Fallback scroll method when Virtuoso ref is not available
 * Uses average row height calculation to better handle variable heights
 */
const fallbackScrollToRow = (virtuosoScroller: Element, rowIndex: number): void => {
  const allRenderedRows = Array.from(virtuosoScroller.querySelectorAll('[data-index]'));

  // Try to find the target row if it's already rendered
  const targetRow = allRenderedRows.find(
    (row) => parseInt((row as HTMLElement).getAttribute('data-index') || '-1') === rowIndex,
  );

  if (targetRow) {
    // Target row is already rendered, scroll to it
    const scrollerRect = virtuosoScroller.getBoundingClientRect();
    const rowRect = targetRow.getBoundingClientRect();
    const isRowVisible = rowRect.top >= scrollerRect.top && rowRect.bottom <= scrollerRect.bottom;

    if (!isRowVisible) {
      // Manually scroll the virtuoso scroller instead of using scrollIntoView to prevent reflow
      const rowTop =
        targetRow.getBoundingClientRect().top - scrollerRect.top + virtuosoScroller.scrollTop;
      const scrollerHeight = scrollerRect.height;
      const rowHeight = rowRect.height;
      const targetScroll = rowTop - scrollerHeight / 2 + rowHeight / 2;

      virtuosoScroller.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
    }
    return;
  }

  // Row not rendered yet - calculate average height from currently rendered rows
  const totalHeight = allRenderedRows.reduce(
    (sum, row) => sum + row.getBoundingClientRect().height,
    0,
  );
  const avgRowHeight =
    allRenderedRows.length > 0 ? totalHeight / allRenderedRows.length : ESTIMATED_ROW_HEIGHT;

  // Estimate scroll position using average height
  const estimatedScrollPosition = rowIndex * avgRowHeight;

  virtuosoScroller.scrollTo({
    top: estimatedScrollPosition,
    behavior: 'auto',
  });

  // Wait for Virtuoso to render, then retry finding the row
  const attemptToFindRow = (attempt: number) => {
    const row = virtuosoScroller.querySelector(`[data-index="${rowIndex}"]`);

    if (row) {
      // Manually scroll instead of using scrollIntoView to prevent reflow
      const scroller =
        (virtuosoScroller.closest('[data-test-id="virtuoso-scroller"]') as HTMLElement) ||
        virtuosoScroller;
      const rowTop =
        row.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
      const scrollerHeight = scroller.getBoundingClientRect().height;
      const rowHeight = row.getBoundingClientRect().height;
      const targetScroll = rowTop - scrollerHeight / 2 + rowHeight / 2;

      scroller.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      });
      return;
    }

    if (attempt >= MAX_ROW_FIND_RETRIES) {
      return;
    }

    // Check what's currently rendered to adjust scroll direction
    const currentRows = Array.from(virtuosoScroller.querySelectorAll('[data-index]'));
    const currentIndices = currentRows.map((r) =>
      parseInt((r as HTMLElement).getAttribute('data-index') || '0'),
    );

    if (currentIndices.length === 0) {
      setTimeout(() => attemptToFindRow(attempt + 1), ROW_FIND_RETRY_DELAY);
      return;
    }

    const minIndex = Math.min(...currentIndices);
    const maxIndex = Math.max(...currentIndices);

    // Recalculate average height with new rendered rows for better accuracy
    const newTotalHeight = currentRows.reduce(
      (sum, row) => sum + row.getBoundingClientRect().height,
      0,
    );
    const newAvgRowHeight = newTotalHeight / currentRows.length;

    // Adjust scroll if target is outside visible range
    if (rowIndex < minIndex) {
      virtuosoScroller.scrollBy({
        top: -newAvgRowHeight * ROW_BATCH_SIZE,
        behavior: 'auto',
      });
    } else if (rowIndex > maxIndex) {
      virtuosoScroller.scrollBy({
        top: newAvgRowHeight * ROW_BATCH_SIZE,
        behavior: 'auto',
      });
    }

    setTimeout(() => attemptToFindRow(attempt + 1), ROW_FIND_RETRY_DELAY);
  };

  setTimeout(() => attemptToFindRow(1), ROW_FIND_RETRY_DELAY);
};
